import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { categorizeProduct } from "../lib/categorizeProduct";
import { isHeicFile, isWebpFile, isUnsupportedUploadFile, UNSUPPORTED_FORMAT_MESSAGE, convertHeicToJpeg } from "../utils/heicConvert";
import { resizeImageIfNeeded } from "../utils/imageResize";
import VariantForm, { ProductImage, Variant } from "./VariantForm";
import { MercadoLibreFicha } from "./MercadoLibreFicha";
import { CampoDeSku, DetallesDelProductoForm } from "./DetallesDelProductoForm";
import { CampoDeCategoriaPropia } from "./CategoriasDelProducto";
import { PanelDeCategorias } from "../pages/lanzamientos/Categorias";
import { useCategorias } from "../pages/lanzamientos/useCategorias";
import { useDetallesDelProducto } from "./useDetallesDelProducto";
import { olvidarTodoElIndice } from "../lib/indiceDeBusquedaDeProductos";
import { hayQueVolverADescribir } from "../lib/detallesDelProducto";
import { buildThumbnailBlob } from "../utils/thumbnail";
import { mensajeRecorte, recortarAlTope } from "../lib/limiteImagenesProducto";

/**
 * Panel "Editar producto" para Mi marca → Mis productos. Espejo de
 * `AddProductFlow`, pero en modo edición: monta `VariantForm` con un
 * `editingVariant` (desktop = panel lateral derecho, mobile/PWA = bottom-sheet,
 * ambos resueltos dentro de `VariantForm`) y porta la MISMA lógica de Supabase
 * que ya usa la pantalla vieja (`SeleccionarProductosAnuncio`):
 *   • renombrar la variante y guardar sus datos (descripción para la IA, categoría,
 *     características, medidas — ADR-2026-10-21-editar-producto-muestra-sus-datos),
 *   • re-categorizar (modo "editar") sólo si cambiaron las fotos,
 *   • subir imagen (HEIC → jpeg, resize, thumbnail opt),
 *   • borrar imagen (storage full+opt → fila), garantizando una principal,
 *   • marcar principal,
 *   • eliminar el producto en cascada (storage → imágenes → variante → producto).
 *
 * `product` es la variante a editar (id + nombre); sus imágenes se cargan al
 * abrir. Al guardar/eliminar refresca la grilla (`onSaved`) y cierra (`onClose`).
 */
export const EditProductFlow = ({
  companyId,
  product,
  onClose,
  onSaved,
  onCategoriasCambiadas,
}: {
  companyId: string | null;
  product: { id: string; name: string };
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  /**
   * El árbol de categorías cambió desde acá (crear, renombrar, mover o borrar). Quien tenga su propia
   * copia del árbol (el filtro de Mi marca) la vuelve a leer. Se avisa en el momento, así que cancelar
   * el editor después no lo pierde (review Greptile del PR #198).
   */
  onCategoriasCambiadas?: () => void;
}) => {
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<
    Record<string, boolean>
  >({});
  // VariantForm exige estos props aunque en modo edición no se usan (son del
  // alta). Mantenemos un placeholder estable.
  const [newProduct, setNewProduct] = useState<{
    nombre: string;
    images: ProductImage[];
  }>({ nombre: "", images: [] });
  const datos = useDetallesDelProducto(product.id, companyId);
  // El árbol de categorías de la marca DEL PRODUCTO (ADR-2026-10-21-categorias-propias-con-subcategorias).
  // Se pide recién con los datos cargados: antes no se sabe de qué marca es.
  const cat = useCategorias(datos.detalles ? companyId : null, datos.brandId);
  // Crear sólo suma una opción; renombrar, mover o borrar cambian lo que se busca de otros productos.
  const crearCategoria = async (nombre: string, madre: string | null) => {
    const r = await cat.crear(nombre, madre);
    if (!r.error) onCategoriasCambiadas?.();
    return r;
  };
  const cambioElArbol = <A extends unknown[]>(accion: (...a: A) => Promise<string | null>) => async (...a: A) => {
    const error = await accion(...a);
    if (!error) {
      olvidarTodoElIndice();
      onCategoriasCambiadas?.();
    }
    return error;
  };
  const [verCategorias, setVerCategorias] = useState(false);
  // Subir, quitar o cambiar la principal: lo único que la categorización en modo editar mira.
  const fotosCambiaron = useRef(false);

  // Cargar las imágenes de la variante al abrir → armar el editingVariant.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("productos_imagenes")
        .select("id, url, principal, orden, variante_id")
        .eq("variante_id", product.id)
        .eq("id_company", companyId)
        .order("orden", { ascending: true });
      const images: ProductImage[] = (data || []).map((img: any) => ({
        id: img.id,
        url: img.url,
        principal: img.principal,
        orden: img.orden,
        producto_variante_id: 0,
        variante_id: img.variante_id,
      }));
      if (!cancelled)
        setEditingVariant({ id: product.id, nombre: product.name, images });
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, product.id, product.name]);

  // ── Guardar nombre y datos (+ categorización modo "editar" si hay fotos nuevas) ──
  const handleSave = async () => {
    if (!editingVariant || !companyId) return;
    const name = (editingVariant.nombre || "").trim();
    if (!name) return; // VariantForm ya valida, doble chequeo defensivo
    try {
      setSaving(true);
      setImageError(null);

      const { error: updError } = await supabase
        .from("variantes")
        .update({ nombre: name })
        .eq("id", editingVariant.id)
        .eq("id_company", companyId);
      if (updError) throw updError;

      const { descripcionVisualEditada } = await datos.guardar();

      // Categorización IA (best-effort): vuelve a describir la foto principal y a clasificar la
      // vista de cada foto. Antes corría en cada "Listo" y pisaba la descripción que la persona
      // acababa de corregir; ahora sólo con fotos nuevas y sin la descripción tocada.
      if (
        hayQueVolverADescribir({
          fotosCambiaron: fotosCambiaron.current,
          descripcionVisualEditada,
        })
      ) {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          const { data: varRow } = await supabase
            .from("variantes")
            .select("product_id")
            .eq("id", editingVariant.id)
            .single();
          const editProductId = (varRow as any)?.product_id;
          if (user && editProductId) {
            categorizeProduct(String(editProductId), {
              modo: "editar",
              userId: user.id,
            });
          }
        } catch {
          /* noop */
        }
      }

      await onSaved();
      onClose();
    } catch (err) {
      console.error("[EditProductFlow] save error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al guardar el producto",
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Subir imagen (HEIC → jpeg, resize, thumbnail opt) ────────────────────
  // Sube N imágenes a la MISMA variante (multi-selección del picker). El loop
  // va acá adentro y no en el componente: `orden` y `principal` se derivan del
  // largo de `editingVariant.images`, que no se actualiza hasta el
  // `refreshVariantImages` final — llamando una vez por archivo, todas
  // quedarían con el mismo `orden` y varias como principal. Con el índice
  // local salen consecutivas y solo la primera puede ser principal.
  const handleImageUpload = async (variantId: string, files: File[]) => {
    if (!companyId || files.length === 0) return;
    try {
      setUploadingImages((prev) => ({ ...prev, [String(variantId)]: true }));
      setImageError(null);

      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      const ordenBase =
        editingVariant?.id === variantId
          ? (editingVariant.images || []).length
          : 0;
      let needsPrincipal =
        editingVariant?.id === variantId &&
        !(editingVariant.images || []).some((i) => i.principal);
      // Una imagen rechazada (tamaño/formato) no aborta las demás: se juntan
      // los nombres y se avisa al final.
      const rejected: string[] = [];
      let uploaded = 0;

      // Tope de 4 por producto. El cupo sale de las fotos que la variante YA
      // tiene: acá se edita un producto existente, así que un lote de 3 sobre una
      // variante con 2 fotos solo puede sumar 2. `ordenBase` ES ese conteo — el
      // único caller (VariantImageManager) siempre pasa el id de la variante en
      // edición, así que nunca cae en la rama `: 0`.
      const { aceptadas: aSubir, rechazadas: sobrantes } = recortarAlTope(
        ordenBase,
        files,
      );
      const avisoTope = mensajeRecorte(sobrantes.length);
      if (avisoTope) rejected.push(avisoTope);

      for (const file of aSubir) {
        if (file.size > MAX_FILE_SIZE) {
          rejected.push(`${file.name} (supera 100 MB)`);
          continue;
        }
        // Formatos bloqueados (RAW/TIFF/GIF/SVG — ADR-590): rebotan con
        // mensaje claro, mismo patrón que el límite de tamaño. Se usa el
        // mensaje compartido, que además dice qué formato SÍ sirve.
        if (isUnsupportedUploadFile(file)) {
          rejected.push(`${file.name}: ${UNSUPPORTED_FORMAT_MESSAGE}`);
          continue;
        }

        let processedFile: File = file;
        if (isHeicFile(file) || isWebpFile(file)) {
          try {
            const converted = await convertHeicToJpeg(file, 0.8);
            processedFile = converted.file;
          } catch {
            throw new Error(
              "No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg",
            );
          }
        }
        processedFile = await resizeImageIfNeeded(processedFile, 2000, 0.85);

        // Jitter con el índice: dos archivos subidos en el mismo milisegundo
        // colisionarían en el path del storage.
        const timestamp = Date.now() + uploaded;
        const sanitizedName = processedFile.name.replace(
          /[^a-zA-Z0-9._-]/g,
          "_",
        );
        const fileBaseName = `${timestamp}_${sanitizedName}`;

        const fullPath = `${companyId}/${variantId}/full/${fileBaseName}`;
        const { error: fullErr } = await supabase.storage
          .from("productos")
          .upload(fullPath, processedFile);
        if (fullErr) throw fullErr;

        // Thumbnail optimizado vía canvas (best-effort).
        try {
          const blob = await buildThumbnailBlob(processedFile);
          if (!blob) throw new Error("no se pudo generar el thumbnail");
          const optPath = `${companyId}/${variantId}/opt/${fileBaseName}`;
          const { error: optErr } = await supabase.storage
            .from("productos")
            .upload(optPath, blob);
          if (optErr)
            console.warn(
              "Optimized upload failed, continuing with full",
              optErr,
            );
        } catch (optErr) {
          console.warn("Error creating optimized image, continuing", optErr);
        }

        const { error: imgInsertError } = await supabase
          .from("productos_imagenes")
          .insert({
            id_company: companyId,
            variante_id: variantId,
            url: fileBaseName,
            principal: needsPrincipal,
            orden: ordenBase + uploaded + 1,
          });
        if (imgInsertError) throw imgInsertError;
        needsPrincipal = false; // solo la primera del lote
        uploaded += 1;
      }

      if (uploaded > 0) await refreshVariantImages(variantId);
      if (rejected.length > 0) {
        setImageError(
          `No pudimos subir ${rejected.length} imagen(es): ${rejected.join(", ")}`,
        );
      }
    } catch (err) {
      console.error("[EditProductFlow] upload error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al subir la imagen",
      );
      // Refrescamos igual: si el lote cortó a la mitad, las que sí entraron
      // tienen que aparecer en la grilla.
      await refreshVariantImages(variantId);
    } finally {
      setUploadingImages((prev) => ({ ...prev, [String(variantId)]: false }));
    }
  };

  // ── Borrar imagen (storage full+opt → fila), dejando una principal ───────
  const handleDeleteImage = async (variantId: string, imageId: number) => {
    if (!companyId) return;
    try {
      const { data: imageData } = await supabase
        .from("productos_imagenes")
        .select("id, url")
        .eq("id", imageId)
        .eq("id_company", companyId)
        .single();
      const filename = (imageData as any)?.url || "";
      if (filename && !filename.startsWith("http")) {
        await supabase.storage
          .from("productos")
          .remove([
            `${companyId}/${variantId}/full/${filename}`,
            `${companyId}/${variantId}/opt/${filename}`,
          ]);
      }

      await supabase
        .from("productos_imagenes")
        .delete()
        .eq("id", imageId)
        .eq("id_company", companyId);

      // Garantizar que quede una principal.
      const { data: remaining } = await supabase
        .from("productos_imagenes")
        .select("id, principal")
        .eq("variante_id", variantId)
        .eq("id_company", companyId)
        .order("orden", { ascending: true });
      if (
        (remaining || []).length > 0 &&
        !(remaining || []).some((i: any) => i.principal)
      ) {
        await supabase
          .from("productos_imagenes")
          .update({ principal: true })
          .eq("id", (remaining as any)[0].id)
          .eq("id_company", companyId);
      }

      await refreshVariantImages(variantId);
    } catch (err) {
      console.error("[EditProductFlow] delete image error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al eliminar la imagen",
      );
    }
  };

  // ── Marcar principal ─────────────────────────────────────────────────────
  const handleSetPrincipalImage = async (
    variantId: string,
    imageId: number,
  ) => {
    if (!companyId) return;
    try {
      await supabase
        .from("productos_imagenes")
        .update({ principal: false })
        .eq("variante_id", variantId)
        .eq("id_company", companyId);
      await supabase
        .from("productos_imagenes")
        .update({ principal: true })
        .eq("id", imageId)
        .eq("id_company", companyId);
      await refreshVariantImages(variantId);
    } catch (err) {
      console.error("[EditProductFlow] set principal error:", err);
      setImageError(
        err instanceof Error
          ? err.message
          : "Error al actualizar la imagen principal",
      );
    }
  };

  // ── Eliminar producto (cascade storage → imágenes → variante → producto) ──
  const handleDeleteVariant = async (variantId: string) => {
    if (!companyId) return;
    try {
      setSaving(true);
      setImageError(null);

      const { data: variantRow, error: variantSelErr } = await supabase
        .from("variantes")
        .select("id, product_id")
        .eq("id", variantId)
        .eq("id_company", companyId)
        .single();
      if (variantSelErr) throw variantSelErr;
      const productId = (variantRow as any)?.product_id as string | null;

      const { data: imagesData } = await supabase
        .from("productos_imagenes")
        .select("id, url")
        .eq("variante_id", variantId)
        .eq("id_company", companyId);
      const filesToRemove: string[] = [];
      (imagesData || []).forEach((img: any) => {
        const filename = (img?.url as string) || "";
        if (filename && !filename.startsWith("http")) {
          filesToRemove.push(`${companyId}/${variantId}/full/${filename}`);
          filesToRemove.push(`${companyId}/${variantId}/opt/${filename}`);
        }
      });
      if (filesToRemove.length > 0) {
        try {
          await supabase.storage.from("productos").remove(filesToRemove);
        } catch {
          /* best-effort */
        }
      }

      await supabase
        .from("productos_imagenes")
        .delete()
        .eq("variante_id", variantId)
        .eq("id_company", companyId);

      await supabase
        .from("variantes")
        .delete()
        .eq("id", variantId)
        .eq("id_company", companyId);

      // Si el producto se queda sin variantes → borrarlo también.
      if (productId) {
        const { data: otherVariants } = await supabase
          .from("variantes")
          .select("id")
          .eq("product_id", productId)
          .eq("id_company", companyId)
          .limit(1);
        if (!otherVariants || otherVariants.length === 0) {
          try {
            await supabase
              .from("productos")
              .delete()
              .eq("id", productId)
              .eq("id_company", companyId);
          } catch {
            /* best-effort */
          }
        }
      }

      await onSaved();
      onClose();
    } catch (err) {
      console.error("[EditProductFlow] delete product error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al eliminar el producto",
      );
    } finally {
      setSaving(false);
    }
  };

  // Re-lee las imágenes de la variante y actualiza el editingVariant del panel.
  const refreshVariantImages = async (variantId: string) => {
    if (!companyId) return;
    fotosCambiaron.current = true;
    const { data } = await supabase
      .from("productos_imagenes")
      .select("id, url, principal, orden, variante_id")
      .eq("variante_id", variantId)
      .eq("id_company", companyId)
      .order("orden", { ascending: true });
    const updatedImages: ProductImage[] = (data || []).map((img: any) => ({
      id: img.id,
      url: img.url,
      principal: img.principal,
      orden: img.orden,
      producto_variante_id: 0,
      variante_id: img.variante_id,
    }));
    setEditingVariant((prev) =>
      prev && prev.id === variantId ? { ...prev, images: updatedImages } : prev,
    );
  };

  // Mientras cargan las imágenes (editingVariant null) no montamos el form.
  if (!editingVariant) return null;

  return (
    <>
      <VariantForm
        editingVariant={editingVariant}
        newProduct={newProduct}
        loading={saving}
        uploadingImages={uploadingImages}
        imageError={imageError}
        onCancel={onClose}
        onSave={handleSave}
        onImageUpload={handleImageUpload}
        onDeleteImage={handleDeleteImage}
        onSetPrincipalImage={handleSetPrincipalImage}
        setEditingVariant={setEditingVariant}
        setNewProduct={setNewProduct}
        onDeleteVariant={handleDeleteVariant}
        debajoDelNombre={
          <>
            <CampoDeSku
              detalles={datos.detalles}
              onChange={datos.setDetalles}
            />
            {datos.conCategoriasPropias && (
              <CampoDeCategoriaPropia
                detalles={datos.detalles}
                onChange={datos.setDetalles}
                categorias={cat.categorias}
                cargando={cat.cargando}
                onCrear={crearCategoria}
                onEditar={() => setVerCategorias(true)}
              />
            )}
          </>
        }
        extraEdicion={
          <>
            <DetallesDelProductoForm
              detalles={datos.detalles}
              onChange={datos.setDetalles}
              conDescripcionDeTienda={datos.conDescripcionDeTienda}
            />
            <MercadoLibreFicha
              varianteId={editingVariant.id}
              companyId={companyId}
            />
          </>
        }
      />
      <PanelDeCategorias
        abierto={verCategorias}
        titulo="Categorías"
        descripcion="Ordenan tus productos y son las mismas de tus lanzamientos. Una subcategoría es una categoría adentro de otra: Remeras › Oversize."
        categorias={cat.categorias}
        cargando={cat.cargando}
        onCerrar={() => {
          setVerCategorias(false);
          // Borrar una categoría desata los productos en cascada: el producto en edición, también.
          datos.podarCategorias(new Set(cat.categorias.map((c) => c.id)));
        }}
        onCrear={crearCategoria}
        onRenombrar={cambioElArbol(cat.renombrar)}
        onMover={cambioElArbol(cat.mover)}
        onBorrar={cambioElArbol(cat.borrar)}
      />
    </>
  );
};

export default EditProductFlow;
